import { FC, useState, useMemo, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Fade } from "@mui/material";
import {
  ModalButton,
  CloseModalButton,
  BackModalButton,
  CurrencyButton,
  SquereButton,
} from "components/Buttons";
import { Select } from "components/Inputs";
import { ModalRow } from "components/Rows";
import {
  AddDeliveryAddressModal,
  ModalWarningInfo,
  CurrencySelectModal,
  ModalError,
} from "components/Modals";
import { SelectItemProps } from "components/Inputs/types";
import { Accordion, AppText, CardImage, Tooltip } from "components";
import {
  virtualCardDesigns,
  extraVirtualCardDesigns,
  colors,
} from "helpers/consts";
import {
  toLocaleStringWithCurrency,
  normolizeCurrenciesDecimals,
} from "helpers/funcs";
import {
  ModalHeading,
  Form,
  InputContainer,
  StatusDecorative,
  InputLineContainer,
} from "../styled";
import type { CostsInterface } from "./";
import type {
  DeliveryAddress,
  CurrencyInterface,
  AvailableTradeOptions,
  WalletsListInterface,
} from "helpers/types";
import type { UsersCard } from "api/types/cardProduct";
import { InfoLine } from "components/Modals/styled";

import { ReactComponent as WalletIcon } from "assets/icons/wallet.svg";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";

interface MainPurchasePlasticStepProps {
  isSubmitting: boolean;
  values: { [field: string]: any };
  onClose: () => void;
  errors: { [field: string]: string };
  handleBlur: (e: any) => void;
  handleChange: (e: any) => void;
  handleSubmit: () => void;
  touched: { [field: string]: boolean };
  addressOptions: SelectItemProps[];
  deliveryMethodsOptions: SelectItemProps[];
  cardInfo: UsersCard | null;
  setNewDeliveryAddresses: (address: DeliveryAddress) => void;
  setNewAddress: (id: number) => void;
  currencies: CurrencyInterface[] | null;
  costInfos: CostsInterface;
  selectedCurrency: AvailableTradeOptions | null;
  walletsList: WalletsListInterface[] | null;
  setSelectedCurrency: Dispatch<SetStateAction<AvailableTradeOptions | null>>;
  hasError: string | null;
  cost: number;
  currencyVariants: AvailableTradeOptions[];
}

const MainPurchasePlasticStep: FC<MainPurchasePlasticStepProps> = ({
  onClose,
  isSubmitting,
  handleSubmit,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  addressOptions,
  deliveryMethodsOptions,
  cardInfo,
  setNewDeliveryAddresses,
  setNewAddress,
  currencies,
  costInfos,
  selectedCurrency,
  walletsList,
  setSelectedCurrency,
  hasError,
  cost,
  currencyVariants,
}) => {
  const { t } = useTranslation();
  const [isAddNewAddressOpen, setIsAddNewAddressOpen] =
    useState<boolean>(false);
  const [isCurrencySelectOpen, setIsCurrencySelectOpen] =
    useState<boolean>(false);

  const currencyBalance = useMemo<number>(() => {
    const leftBalance = walletsList?.find(
      ({ currencyName }) => selectedCurrency === currencyName
    )?.balance;
    return !selectedCurrency || !leftBalance
      ? 0
      : Number(
          normolizeCurrenciesDecimals(leftBalance, selectedCurrency, currencies)
        );
  }, [walletsList, selectedCurrency, currencies]);

  const purchasePlasticCurrencyVariants = useMemo<
    AvailableTradeOptions[]
  >(() => {
    if (walletsList?.find(({ currencyName }) => currencyName === "EUR")) {
      return [...currencyVariants, "EUR"];
    }

    return currencyVariants;
  }, [walletsList, currencyVariants]);

  return (
    <>
      <Fade in timeout={500}>
        <Form onSubmit={handleSubmit}>
          <ModalHeading $centered>{t("ORDER_PLASTIC_CARD")}</ModalHeading>
          <CloseModalButton onClose={onClose} />
          <BackModalButton onBack={onClose} />

          <StatusDecorative $noIcon>
            <CardImage
              src={
                cardInfo
                  ? cardInfo?.card.designCode === "CA02"
                    ? extraVirtualCardDesigns[cardInfo?.card.cryptoCurrencyName]
                        ?.image || ""
                    : virtualCardDesigns[cardInfo?.card.designCode]?.image || ""
                  : ""
              }
              alt={cardInfo?.cardProductTemplate.cardBrand || "card"}
            />
          </StatusDecorative>

          <InputContainer>
            <AppText>{t("DELIVERY_ADDRESS")}</AppText>
            <InputLineContainer>
              <Select
                name="deliveryAddressId"
                placeholder=""
                variants={addressOptions}
                value={values.deliveryAddressId}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={addressOptions.length < 1}
                error={
                  touched.deliveryAddressId && Boolean(errors.deliveryAddressId)
                }
                helperText={
                  touched.deliveryAddressId && t(errors.deliveryAddressId || "")
                }
              />
              <Tooltip title={t("ADD_NEW_DELIVERY_ADDRESS")}>
                <SquereButton
                  $view="forInput"
                  onClick={() => setIsAddNewAddressOpen(true)}
                >
                  <EditIcon />
                </SquereButton>
              </Tooltip>
            </InputLineContainer>
          </InputContainer>

          <ModalWarningInfo text={t("DELIVERY_RESTRICTIONS")} />

          <InputContainer style={{ marginTop: "1.6rem" }}>
            <AppText color={colors.gray_400} fontWeight={400}>
              {t("CHOOSE_METHOD_ASSET")}
            </AppText>

            <CurrencyButton
              onClick={() => setIsCurrencySelectOpen(true)}
              value={selectedCurrency || undefined}
              icon={selectedCurrency || undefined}
            />
            <InfoLine>
              <WalletIcon />
              <AppText color={colors.gray_600} fontWeight={400} fontSize={12}>
                {t("BALANCE")} {currencyBalance} {selectedCurrency}
              </AppText>
            </InfoLine>
          </InputContainer>

          <InputContainer>
            <AppText>{t("DELIVERY_METHOD")}</AppText>
            <Select
              name="deliveryMethodId"
              placeholder=""
              variants={deliveryMethodsOptions}
              value={values.deliveryMethodId}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.deliveryMethodId && Boolean(errors.deliveryMethodId)
              }
              helperText={
                touched.deliveryMethodId && t(errors.deliveryMethodId || "")
              }
            />
          </InputContainer>

          <Accordion
            label={t("TOTAL_PRICE")}
            subLabel={`${normolizeCurrenciesDecimals(
              costInfos.cardCost + costInfos.deliveryMethodCost,
              selectedCurrency || "USDT",
              currencies,
              "forView"
            )} ${selectedCurrency || "USDT"}`}
          >
            <ModalRow
              keyText={t("RATE")}
              value={`≈ ${toLocaleStringWithCurrency(
                cost,
                cardInfo?.card.currencyName || "EUR",
                currencies
              )}`}
              isBold
            />
            <ModalRow
              keyText={t("CARD_COST")}
              value={toLocaleStringWithCurrency(
                costInfos.cardCost,
                selectedCurrency || "USDT"
              )}
            />
            <ModalRow
              keyText={t("DELIVERY_COST")}
              value={toLocaleStringWithCurrency(
                costInfos.deliveryMethodCost,
                selectedCurrency || "USDT"
              )}
            />
          </Accordion>

          {hasError && (
            <ModalError style={{ marginTop: "1rem" }} text={t(hasError)} />
          )}

          <ModalButton
            variant="contained"
            fullWidth
            type="submit"
            style={{ marginTop: "1rem" }}
            disabled={isSubmitting || !!hasError}
          >
            {t("TO_ORDER")}
          </ModalButton>
        </Form>
      </Fade>

      <AddDeliveryAddressModal
        isOpen={isAddNewAddressOpen}
        onClose={() => setIsAddNewAddressOpen(false)}
        setNewDeliveryAddresses={setNewDeliveryAddresses}
        setNewAddress={setNewAddress}
      />

      <CurrencySelectModal
        isOpen={isCurrencySelectOpen}
        onClose={() => setIsCurrencySelectOpen(false)}
        options={purchasePlasticCurrencyVariants}
        selected={selectedCurrency || null}
        setValue={setSelectedCurrency}
      />
    </>
  );
};

export default MainPurchasePlasticStep;
